import { clsx } from 'clsx'
import type { InstagramPreviewProfile } from '~publish/components/Composer/InstagramPreview/types'

import { useMixedMediaCapabilityForService } from '~publish/components/Composer/capability'
import { usePreviewEnvironment } from '~publish/components/Composer/components/ChannelPostPreview/PreviewEnvironment'
import AppStore from '~publish/legacy/composer/composer/stores/AppStore'
import { SERVICE_INSTAGRAM } from '~publish/legacy/constants'
import { PostTypeReel, PostTypeStory } from '~publish/legacy/post/constants'

import { Empty } from '../Common/Empty'
import { InstagramPost } from './components/Post'
import { getMediaProperties } from '../Common/utils'
import type {
  DraftPreview,
  InstagramChannelData,
  InstagramPostType,
} from '../types'
import { Reel } from './components/Reel'
import { Story } from './components/Story'
import commonStyles from '../Common/Common.module.css'
import { PostPreviewCard } from '../Common/styles'

import styles from './InstagramPreview.module.css'

import type { JSX } from 'react'

const isInstagramChannelData = (
  value: unknown,
): value is InstagramChannelData =>
  typeof value === 'object' && value !== null && 'instagram' in value

const Content = ({
  draftInstagram,
  profile,
  updateType,
  forceReel,
}: {
  draftInstagram: InstagramPostType
  profile: InstagramPreviewProfile
  updateType: string | null
  forceReel: boolean
}): JSX.Element => {
  if (updateType === PostTypeReel || forceReel) {
    return <Reel content={draftInstagram.content} profile={profile} />
  }

  if (updateType === PostTypeStory) {
    return <Story content={draftInstagram.content} profile={profile} />
  }

  return <InstagramPost content={draftInstagram.content} profile={profile} />
}

export const InstagramPreview = ({
  draft,
  profile,
}: {
  draft: DraftPreview
  profile: InstagramPreviewProfile
}): JSX.Element => {
  const { readComposerStores } = usePreviewEnvironment()
  const isOmniboxEnabled =
    readComposerStores && Boolean(AppStore.getIsOmniboxEnabled())
  const capability = useMixedMediaCapabilityForService(SERVICE_INSTAGRAM)

  const draftContent = { ...draft }
  const draftChannelData =
    'channelData' in draft ? draft.channelData : undefined
  if ('channelData' in draftContent) {
    delete draftContent.channelData
  }
  const channelData = isInstagramChannelData(draftChannelData)
    ? draftChannelData
    : undefined
  const draftInstagram: InstagramPostType = {
    content: {
      ...draftContent,
      channelData,
      text: draft.text,
      isReminder: draft.isReminder,
    },
  }

  if (draft.isEmpty) return <Empty />

  const { hasImages, hasGif, hasVideo } = getMediaProperties(
    draftInstagram.content,
    null,
  )
  // A Reel can't coexist with other attached media, so only force the Reel
  // layout when the video is the sole attachment — a video alongside images
  // or a gif is a mixed carousel wherever mixing is allowed.
  const forceReel =
    hasVideo &&
    isOmniboxEnabled &&
    (!capability.canMixMediaTypes || (!hasImages && !hasGif))

  return (
    <PostPreviewCard
      data-testid="instagram-preview"
      className={clsx(
        styles.instagramPreview,
        hasVideo &&
          (draft.updateType === PostTypeReel ||
            draft.updateType === PostTypeStory ||
            forceReel) &&
          commonStyles.videoPreview,
      )}
    >
      <Content
        draftInstagram={draftInstagram}
        profile={profile}
        updateType={draft.updateType}
        forceReel={forceReel}
      />
    </PostPreviewCard>
  )
}
